import React, { useState, useEffect, useCallback } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { getSupabaseClient } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Switch } from '../ui/switch';
import { 
  Building2, 
  User, 
  Receipt, 
  Globe, 
  MapPin, 
  AlertCircle, 
  CheckCircle2, 
  Loader2, 
  Save, 
  Compass, 
  ShieldCheck,
  IndianRupee,
  Sliders,
  Printer 
} from 'lucide-react';
import { toast } from 'sonner';
import { ProviderProfileForm } from './ProviderProfileForm';
import { ServicesPrintSettingsView } from './ServicesPrintSettingsView';

const PROVIDER_TYPES = ['Rider', 'Cleaner', 'Cook', 'Plumber', 'Electrician', 'Other'];

export const ServicesAdminSettings: React.FC = () => {
  const { currentTenantId, activeBranchId, user } = useAuth();
  const supabase = getSupabaseClient();
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const getInitialTab = (): 'general' | 'booking' | 'print' => {
    const path = location.pathname.toLowerCase();
    if (path.includes('print-settings')) return 'print';
    const tabParam = searchParams.get('tab');
    if (tabParam === 'print') return 'print';
    if (tabParam === 'booking' || tabParam === 'booking-online') return 'booking';
    if (tabParam === 'general') return 'general';
    return 'general';
  };

  const [activeSettingsTab, setActiveSettingsTab] = useState<'general' | 'booking' | 'print'>(getInitialTab);

  useEffect(() => {
    const path = location.pathname.toLowerCase();
    if (path.includes('print-settings')) {
      setActiveSettingsTab('print');
    } else {
      const tabParam = searchParams.get('tab');
      if (tabParam === 'print') {
        setActiveSettingsTab('print');
      } else if (tabParam === 'booking' || tabParam === 'booking-online') {
        setActiveSettingsTab('booking');
      } else if (tabParam === 'general') {
        setActiveSettingsTab('general');
      }
    }
  }, [location.pathname, searchParams]);

  const handleTabChange = (tab: 'general' | 'booking' | 'print') => {
    setActiveSettingsTab(tab);
    if (tab === 'print') {
      navigate('/merchant-dashboard/services/print-settings');
    } else if (tab === 'booking') {
      navigate('/merchant-dashboard/services/settings?tab=booking');
    } else {
      navigate('/merchant-dashboard/services/settings');
    }
  };

  const [loading, setLoading] = useState(true);
  const [savingGst, setSavingGst] = useState(false);
  const [savingBranch, setSavingBranch] = useState(false);
  const [savingMode, setSavingMode] = useState(false);

  // 1. Service Provider Mode & Profile
  const [serviceMode, setServiceMode] = useState<string | null>(null);
  const [myProfile, setMyProfile] = useState<any>(null);

  // 2. Billing & GST Settings (service_billing_settings)
  const [gstEnabled, setGstEnabled] = useState(false);
  const [gstin, setGstin] = useState('');

  // 3. Branch & Public Booking (branches table)
  const [allBranches, setAllBranches] = useState<any[]>([]);
  const [currentBranch, setCurrentBranch] = useState<any>(null);
  const [isOnlineBookingActive, setIsOnlineBookingActive] = useState(false);
  const [isPubliclyVisible, setIsPubliclyVisible] = useState<boolean>(true);
  const [branchAddress, setBranchAddress] = useState('');
  const [branchCity, setBranchCity] = useState('');
  const [branchState, setBranchState] = useState('');
  const [branchPincode, setBranchPincode] = useState('');
  const [branchLat, setBranchLat] = useState<string>('');
  const [branchLng, setBranchLng] = useState<string>('');

  // 4. Branch Booking Charge (Minimum Amount) Settings
  const [chargeBranchId, setChargeBranchId] = useState<string>('');
  const [chargeEnabled, setChargeEnabled] = useState(false);
  const [chargeType, setChargeType] = useState<'fixed' | 'percent'>('fixed');
  const [chargeValue, setChargeValue] = useState('');
  const [chargeAppliesTo, setChargeAppliesTo] = useState<'online' | 'offline' | 'both'>('both');
  const [applyToAllBranches, setApplyToAllBranches] = useState(false);
  const [savingCharge, setSavingCharge] = useState(false);

  const loadAllSettings = useCallback(async () => {
    if (!currentTenantId || !supabase) return;
    setLoading(true);

    try {
      // 1. Fetch Tenant service_mode
      const { data: tenantData } = await supabase
        .from('tenants')
        .select('service_mode')
        .eq('id', currentTenantId)
        .single();
      if (tenantData) {
        setServiceMode(tenantData.service_mode || 'business');
      }

      // 2. Fetch service_billing_settings
      const { data: billData } = await supabase
        .from('service_billing_settings')
        .select('*')
        .eq('tenant_id', currentTenantId)
        .maybeSingle();
      if (billData) {
        setGstEnabled(Boolean(billData.gst_enabled));
        setGstin(billData.gstin || '');
      }

      // 3. Fetch all branches of tenant
      const { data: allBData } = await supabase
        .from('branches')
        .select('*')
        .eq('tenant_id', currentTenantId)
        .order('branch_name', { ascending: true });

      const bList = allBData || [];
      setAllBranches(bList);

      const targetBranch = (activeBranchId ? bList.find((b: any) => b.id === activeBranchId) : null) || bList[0];
      if (targetBranch) {
        setCurrentBranch(targetBranch);
        setIsOnlineBookingActive(Boolean(targetBranch.is_online_booking_active));
        setBranchAddress(targetBranch.address || '');
        setBranchCity(targetBranch.city || '');
        setBranchState(targetBranch.state || '');
        setBranchPincode(targetBranch.pincode || '');
        setBranchLat(targetBranch.latitude != null ? String(targetBranch.latitude) : '');
        setBranchLng(targetBranch.longitude != null ? String(targetBranch.longitude) : '');

        const branchIdToUse = targetBranch.id;
        setChargeBranchId(branchIdToUse);

        // Fetch booking charge settings for this branch
        try {
          const { data: chargeData, error: chargeErr } = await supabase.rpc('get_branch_booking_charge_settings', {
            p_branch_id: branchIdToUse
          });
          if (!chargeErr && chargeData) {
            setChargeEnabled(Boolean(chargeData.enabled));
            setChargeType(chargeData.type === 'percent' ? 'percent' : 'fixed');
            setChargeValue(chargeData.value !== null && chargeData.value !== undefined ? String(chargeData.value) : '');
            setChargeAppliesTo(chargeData.applies_to || 'both');
          }
        } catch (ce) {
          console.warn('Could not load branch booking charge settings:', ce);
        }
      }

      // 4. Fetch myProfile if individual
      if (user) {
        const { data: spData } = await supabase
          .from('service_providers')
          .select('*')
          .eq('tenant_id', currentTenantId);
        if (spData && spData.length > 0) {
          let found = spData.find((p: any) => p.user_id === user.id) || spData[0];
          if (found && !found.provider_type) found.provider_type = 'Rider';
          setMyProfile(found);
        }
      }
    } catch (err: any) {
      console.error('Error loading services settings:', err);
      toast.error(err.message || 'Failed to load settings');
    } finally {
      setLoading(false);
    }
  }, [currentTenantId, activeBranchId, supabase, user]);

  useEffect(() => {
    loadAllSettings();
  }, [loadAllSettings]);

  // Save GST & Billing Settings
  const handleSaveGst = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!supabase || !currentTenantId) return;

    const trimmedGstin = gstin.trim().toUpperCase();

    // Basic format check if GST is enabled
    if (gstEnabled && trimmedGstin) {
      if (trimmedGstin.length !== 15) {
        toast.error('GSTIN must be exactly 15 characters (e.g. 29ABCDE1234F1Z5)');
        return;
      }
    }

    setSavingGst(true);
    try {
      const { error } = await supabase
        .from('service_billing_settings')
        .upsert({
          tenant_id: currentTenantId,
          gst_enabled: gstEnabled,
          gstin: trimmedGstin || null,
          updated_at: new Date().toISOString()
        }, { onConflict: 'tenant_id' });

      if (error) throw error;
      toast.success('GST & Billing preferences saved successfully');
    } catch (err: any) {
      console.error('Error saving GST settings:', err);
      // Surface exact backend error text (never swallow)
      toast.error(err.message || 'Failed to save GST settings');
    } finally {
      setSavingGst(false);
    }
  };

  // Save Branch Location & Online Booking Switch
  const handleSaveBranch = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!supabase || !currentBranch?.id) {
      toast.error('No active branch selected');
      return;
    }

    setSavingBranch(true);
    try {
      const { error } = await supabase
        .from('branches')
        .update({
          is_online_booking_active: isOnlineBookingActive,
          address: branchAddress.trim() || null,
          city: branchCity.trim() || null,
          state: branchState.trim() || null,
          pincode: branchPincode.trim() || null,
          latitude: branchLat ? parseFloat(branchLat) : null,
          longitude: branchLng ? parseFloat(branchLng) : null,
          updated_at: new Date().toISOString()
        })
        .eq('id', currentBranch.id);

      if (error) throw error;
      toast.success('Branch & Public Discovery settings updated successfully');
    } catch (err: any) {
      console.error('Error updating branch settings:', err);
      // Surface exact backend error text
      toast.error(err.message || 'Failed to update branch settings');
    } finally {
      setSavingBranch(false);
    }
  };

  // Load booking charge settings on branch change
  const loadBranchChargeSettings = useCallback(async (bId: string) => {
    if (!supabase || !bId) return;
    try {
      const { data, error } = await supabase.rpc('get_branch_booking_charge_settings', {
        p_branch_id: bId
      });
      if (error) {
        console.error('Error fetching branch booking charge settings:', error);
        toast.error(error.message || 'Failed to load booking charge settings');
        return;
      }
      if (data) {
        setChargeEnabled(Boolean(data.enabled));
        setChargeType(data.type === 'percent' ? 'percent' : 'fixed');
        setChargeValue(data.value !== null && data.value !== undefined ? String(data.value) : '');
        setChargeAppliesTo(data.applies_to || 'both');
      }
    } catch (err: any) {
      console.error('loadBranchChargeSettings error:', err);
    }
  }, [supabase]);

  const handleSelectChargeBranch = (bId: string) => {
    setChargeBranchId(bId);
    loadBranchChargeSettings(bId);
  };

  // Save Branch Booking Charge Settings
  const handleSaveChargeSettings = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!supabase || !chargeBranchId) {
      toast.error('Please select a branch first');
      return;
    }

    let parsedVal = 0;
    if (chargeEnabled) {
      parsedVal = parseFloat(chargeValue);
      if (isNaN(parsedVal) || parsedVal <= 0) {
        toast.error('Booking charge value must be greater than 0');
        return;
      }
      if (chargeType === 'percent' && parsedVal > 100) {
        toast.error('Booking charge percentage cannot exceed 100%');
        return;
      }
    } else {
      parsedVal = parseFloat(chargeValue) || 0;
    }

    setSavingCharge(true);
    try {
      const { data, error } = await supabase.rpc('save_branch_booking_charge_settings', {
        p_branch_id: chargeBranchId,
        p_enabled: chargeEnabled,
        p_type: chargeType,
        p_value: parsedVal,
        p_applies_to: chargeAppliesTo,
        p_apply_to_all_branches: applyToAllBranches
      });

      if (error) {
        console.error('Error saving branch booking charge:', error);
        toast.error(error.message || 'Failed to save booking charge settings');
        return;
      }

      toast.success(applyToAllBranches
        ? 'Booking charge rule saved and applied to all branches!'
        : 'Branch booking charge settings saved successfully!'
      );
    } catch (err: any) {
      console.error('Error saving booking charge:', err);
      toast.error(err.message || 'Failed to save booking charge settings');
    } finally {
      setSavingCharge(false);
    }
  };

  // Toggle Online Booking switch directly via set_service_online_booking
  const handleToggleOnlineBooking = async (active: boolean) => {
    if (!supabase || !currentBranch?.id) return;
    setIsOnlineBookingActive(active);

    try {
      const { data, error } = await supabase.rpc('set_service_online_booking', {
        p_branch_id: currentBranch.id,
        p_active: active
      });

      if (error) {
        console.error('set_service_online_booking error:', error);
        setIsOnlineBookingActive(!active);
        toast.error(error.message || 'Failed to update online booking switch');
        return;
      }

      if (data) {
        setIsOnlineBookingActive(Boolean(data.is_online_booking_active));
        setIsPubliclyVisible(Boolean(data.publicly_visible));
      }
      toast.success(active ? 'Public App bookings enabled for this branch!' : 'Public App bookings disabled.');
    } catch (err: any) {
      console.error('Error toggling online booking:', err);
      setIsOnlineBookingActive(!active);
      toast.error(err.message || 'Failed to update online booking switch');
    }
  };

  // Detect Current Location
  const handleDetectLocation = () => {
    if (!navigator.geolocation) {
      toast.error('Geolocation is not supported by your browser');
      return;
    }

    toast.info('Fetching GPS location...');
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setBranchLat(pos.coords.latitude.toFixed(6));
        setBranchLng(pos.coords.longitude.toFixed(6));
        toast.success(`Coordinates detected: ${pos.coords.latitude.toFixed(4)}, ${pos.coords.longitude.toFixed(4)}`);
      },
      (err) => {
        console.error('GPS error:', err);
        toast.error('Location permission denied or unavailable: ' + err.message);
      },
      { timeout: 10000, enableHighAccuracy: true }
    );
  };

  // Toggle Provider Mode (Business vs Individual)
  const handleSelectMode = async (mode: 'individual' | 'business') => {
    if (!supabase || !currentTenantId) return;

    if (serviceMode === 'business' && mode === 'individual') {
      if (!window.confirm("You are switching from Business to Individual mode. This will only show your own profile — existing staff records are kept safe but hidden in the UI. Proceed?")) return;
    }

    setSavingMode(true);
    try {
      const { error } = await supabase
        .from('tenants')
        .update({ service_mode: mode })
        .eq('id', currentTenantId);

      if (error) throw error;
      setServiceMode(mode);
      toast.success('Provider mode updated successfully');
    } catch (err: any) {
      console.error('service_mode update failed:', err);
      toast.error(err.message || 'Failed to save mode');
    } finally {
      setSavingMode(false);
    }
  };

  if (loading) {
    return (
      <div className="p-12 flex flex-col items-center justify-center text-slate-400 text-xs gap-2">
        <Loader2 size={24} className="animate-spin text-primary" />
        <span>Loading Services Settings...</span>
      </div>
    );
  }

  return (
    <div className="flex-1 p-4 sm:p-6 h-full overflow-auto bg-slate-50 dark:bg-slate-900 space-y-6">
      {/* Title */}
      <div>
        <h1 className="text-xl font-bold text-slate-900 dark:text-white flex items-center gap-2">
          <span>Daily Services — Settings</span>
        </h1>
        <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
          Manage GST billing preferences, booking charges, public discovery, and invoice printing styles.
        </p>
      </div>

      {/* Tabs / Section Bar */}
      <div className="flex items-center gap-2 border-b border-slate-200 dark:border-slate-800 pb-2">
        <button
          type="button"
          onClick={() => handleTabChange('general')}
          className={`flex items-center gap-2 px-4 py-2 rounded-lg text-xs font-bold transition-colors ${
            activeSettingsTab === 'general'
              ? 'bg-primary text-white shadow-xs'
              : 'text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 hover:text-slate-900 dark:hover:text-white'
          }`}
        >
          <Sliders size={14} />
          <span>General</span>
        </button>

        <button
          type="button"
          onClick={() => handleTabChange('booking')}
          className={`flex items-center gap-2 px-4 py-2 rounded-lg text-xs font-bold transition-colors ${
            activeSettingsTab === 'booking'
              ? 'bg-primary text-white shadow-xs'
              : 'text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 hover:text-slate-900 dark:hover:text-white'
          }`}
        >
          <Globe size={14} />
          <span>Booking & Online</span>
        </button>

        <button
          type="button"
          onClick={() => handleTabChange('print')}
          className={`flex items-center gap-2 px-4 py-2 rounded-lg text-xs font-bold transition-colors ${
            activeSettingsTab === 'print'
              ? 'bg-primary text-white shadow-xs'
              : 'text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 hover:text-slate-900 dark:hover:text-white'
          }`}
        >
          <Printer size={14} />
          <span>Print Settings</span>
        </button>
      </div>

      {/* ======================================================== */}
      {/* TAB 1: GENERAL SETTINGS                                  */}
      {/* ======================================================== */}
      {activeSettingsTab === 'general' && (
        <div className="max-w-4xl space-y-6">
        {/* ======================================================== */}
        {/* SECTION 1: GST & SMART BILLING SETTINGS                  */}
        {/* ======================================================== */}
        <Card className="border border-slate-200 dark:border-slate-800 shadow-sm bg-white dark:bg-slate-900">
          <CardHeader className="p-5 pb-3 border-b border-slate-100 dark:border-slate-800">
            <div className="flex items-center gap-2.5">
              <div className="p-2 rounded-xl bg-blue-50 dark:bg-blue-950/40 text-blue-600 dark:text-blue-400">
                <Receipt size={18} />
              </div>
              <div>
                <CardTitle className="text-sm font-bold text-slate-900 dark:text-white">
                  GST & Invoicing Configuration
                </CardTitle>
                <CardDescription className="text-xs text-slate-500">
                  Control whether GST is levied on service invoices and manage your 15-character GSTIN.
                </CardDescription>
              </div>
            </div>
          </CardHeader>

          <CardContent className="p-5">
            <form onSubmit={handleSaveGst} className="space-y-4">
              <div className="flex items-center justify-between p-3.5 rounded-xl bg-slate-50 dark:bg-slate-800/60 border border-slate-200 dark:border-slate-700">
                <div className="space-y-0.5">
                  <span className="text-xs font-bold text-slate-800 dark:text-slate-200 block">
                    GST on Invoices
                  </span>
                  <p className="text-[11px] text-slate-500 dark:text-slate-400">
                    {gstEnabled 
                      ? 'Invoices will automatically compute CGST/SGST or IGST based on branch state.'
                      : 'GST is turned OFF. Invoices will have 0% GST (CGST/SGST amounts = ₹0).'
                    }
                  </p>
                </div>
                <Switch
                  checked={gstEnabled}
                  onCheckedChange={setGstEnabled}
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-slate-700 dark:text-slate-300 mb-1">
                  Business GSTIN {gstEnabled && <span className="text-rose-500">* (Mandatory for GST Invoices)</span>}
                </label>
                <Input
                  type="text"
                  maxLength={15}
                  value={gstin}
                  onChange={e => setGstin(e.target.value.toUpperCase())}
                  placeholder="e.g. 29ABCDE1234F1Z5"
                  className="h-10 text-xs font-mono uppercase bg-slate-50 dark:bg-slate-800/80"
                />
                <p className="text-[10px] text-slate-400 mt-1">
                  15-character Goods and Services Tax Identification Number. Leave blank if not registered.
                </p>
              </div>

              <div className="flex justify-end pt-1">
                <Button
                  type="submit"
                  disabled={savingGst}
                  className="bg-primary hover:bg-primary/90 text-white text-xs h-9 px-5 font-semibold flex items-center gap-1.5"
                >
                  {savingGst ? <Loader2 size={13} className="animate-spin" /> : <Save size={13} />}
                  <span>Save GST Settings</span>
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>

        {/* ======================================================== */}
        {/* SECTION 3: PROVIDER TYPE & INDIVIDUAL / BUSINESS MODE     */}
        {/* ======================================================== */}
        <Card className="border border-slate-200 dark:border-slate-800 shadow-sm bg-white dark:bg-slate-900">
          <CardHeader className="p-5 pb-3 border-b border-slate-100 dark:border-slate-800">
            <CardTitle className="text-sm font-bold text-slate-900 dark:text-white">
              Provider Mode & Team Structure
            </CardTitle>
            <CardDescription className="text-xs text-slate-500">
              Choose how you want to manage services. This affects the "Staff & Team" views and assignment capabilities.
            </CardDescription>
          </CardHeader>
          <CardContent className="p-5 space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div 
                className={`border-2 rounded-xl p-4 cursor-pointer transition-all ${
                  serviceMode === 'individual' ? 'border-primary bg-primary/5' : 'border-slate-200 dark:border-slate-700 hover:border-primary/50'
                }`}
                onClick={() => handleSelectMode('individual')}
              >
                <div className="flex items-center gap-3 mb-2">
                  <User className={`w-5 h-5 ${serviceMode === 'individual' ? 'text-primary' : 'text-slate-400'}`} />
                  <h3 className="font-semibold text-sm">Individual Professional</h3>
                </div>
                <p className="text-xs text-slate-500 dark:text-slate-400">
                  I work alone. Manage your own profile, vehicle, and documents directly.
                </p>
                {serviceMode === 'individual' && (
                  <span className="mt-2.5 inline-block text-[10px] font-bold text-primary bg-primary/10 px-2 py-0.5 rounded">
                    Active
                  </span>
                )}
              </div>

              <div 
                className={`border-2 rounded-xl p-4 cursor-pointer transition-all ${
                  serviceMode === 'business' ? 'border-emerald-500 bg-emerald-50 dark:bg-emerald-950/20' : 'border-slate-200 dark:border-slate-700 hover:border-emerald-500/50'
                }`}
                onClick={() => handleSelectMode('business')}
              >
                <div className="flex items-center gap-3 mb-2">
                  <Building2 className={`w-5 h-5 ${serviceMode === 'business' ? 'text-emerald-600' : 'text-slate-400'}`} />
                  <h3 className="font-semibold text-sm">Business / Team</h3>
                </div>
                <p className="text-xs text-slate-500 dark:text-slate-400">
                  I manage a team. Add, dispatch, and track multiple technicians, staff members, and supervisors.
                </p>
                {serviceMode === 'business' && (
                  <span className="mt-2.5 inline-block text-[10px] font-bold text-emerald-600 bg-emerald-100 dark:bg-emerald-900/40 px-2 py-0.5 rounded">
                    Active
                  </span>
                )}
              </div>
            </div>

            {serviceMode === 'individual' && (
              <ProviderProfileForm 
                profile={myProfile} 
                setProfile={setMyProfile}
                user={user}
                currentTenantId={currentTenantId || ''}
                onProfileSaved={loadAllSettings}
              />
            )}
          </CardContent>
        </Card>
      </div>
      )}

      {/* ======================================================== */}
      {/* TAB 2: BOOKING & ONLINE SETTINGS                         */}
      {/* ======================================================== */}
      {activeSettingsTab === 'booking' && (
        <div className="max-w-4xl space-y-6">
        {/* ======================================================== */}
        {/* SECTION: BOOKING CHARGE (MINIMUM AMOUNT)                 */}
        {/* ======================================================== */}
        <Card className="border border-slate-200 dark:border-slate-800 shadow-sm bg-white dark:bg-slate-900">
          <CardHeader className="p-5 pb-3 border-b border-slate-100 dark:border-slate-800">
            <div className="flex items-center gap-2.5">
              <div className="p-2 rounded-xl bg-indigo-50 dark:bg-indigo-950/40 text-indigo-600 dark:text-indigo-400">
                <IndianRupee size={18} />
              </div>
              <div>
                <CardTitle className="text-sm font-bold text-slate-900 dark:text-white">
                  Booking charge (minimum amount)
                </CardTitle>
                <CardDescription className="text-xs text-slate-500">
                  Configure branch-level minimum booking advance required from customers.
                </CardDescription>
              </div>
            </div>
          </CardHeader>

          <CardContent className="p-5">
            <form onSubmit={handleSaveChargeSettings} className="space-y-4">
              {/* Branch Selector */}
              {allBranches.length > 1 && (
                <div>
                  <label className="block text-xs font-semibold text-slate-700 dark:text-slate-300 mb-1">
                    Select Branch
                  </label>
                  <select
                    value={chargeBranchId}
                    onChange={(e) => handleSelectChargeBranch(e.target.value)}
                    className="w-full h-10 rounded-md border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/80 px-3 text-xs dark:text-white focus:outline-none focus:ring-1 focus:ring-primary font-medium"
                  >
                    {allBranches.map((b) => (
                      <option key={b.id} value={b.id}>
                        {b.branch_name} {b.city ? `(${b.city})` : ''}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              {/* Enable Switch */}
              <div className="flex items-center justify-between p-3.5 rounded-xl bg-slate-50 dark:bg-slate-800/60 border border-slate-200 dark:border-slate-700">
                <div className="space-y-0.5">
                  <span className="text-xs font-bold text-slate-800 dark:text-slate-200 block">
                    Require Minimum Booking Amount
                  </span>
                  <p className="text-[11px] text-slate-500 dark:text-slate-400">
                    Customer pays at least this amount when booking. If the final bill is higher it is adjusted in the bill; if the work is smaller, this minimum is still charged.
                  </p>
                </div>
                <Switch
                  checked={chargeEnabled}
                  onCheckedChange={setChargeEnabled}
                />
              </div>

              {chargeEnabled && (
                <div className="space-y-4 pt-1">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    {/* Charge Type: Segmented Fixed amount / Percentage */}
                    <div>
                      <label className="block text-xs font-semibold text-slate-700 dark:text-slate-300 mb-1.5">
                        Charge Calculation Type
                      </label>
                      <div className="grid grid-cols-2 gap-1 p-1 bg-slate-100 dark:bg-slate-800 rounded-lg text-xs font-semibold">
                        <button
                          type="button"
                          onClick={() => setChargeType('fixed')}
                          className={`py-1.5 px-3 rounded-md transition-colors text-center ${
                            chargeType === 'fixed'
                              ? 'bg-white dark:bg-slate-700 text-primary shadow-xs'
                              : 'text-slate-600 dark:text-slate-400 hover:text-slate-900'
                          }`}
                        >
                          Fixed amount (₹)
                        </button>
                        <button
                          type="button"
                          onClick={() => setChargeType('percent')}
                          className={`py-1.5 px-3 rounded-md transition-colors text-center ${
                            chargeType === 'percent'
                              ? 'bg-white dark:bg-slate-700 text-primary shadow-xs'
                              : 'text-slate-600 dark:text-slate-400 hover:text-slate-900'
                          }`}
                        >
                          Percentage (%)
                        </button>
                      </div>
                    </div>

                    {/* Value Input */}
                    <div>
                      <label className="block text-xs font-semibold text-slate-700 dark:text-slate-300 mb-1.5">
                        {chargeType === 'fixed' ? 'Fixed Amount (₹) *' : 'Percentage of Total (%) *'}
                      </label>
                      <Input
                        type="number"
                        min={0}
                        max={chargeType === 'percent' ? 100 : undefined}
                        step="any"
                        value={chargeValue}
                        onChange={(e) => setChargeValue(e.target.value)}
                        placeholder={chargeType === 'fixed' ? 'e.g. 500' : 'e.g. 10'}
                        className="h-10 text-xs font-mono font-bold bg-slate-50 dark:bg-slate-800/80"
                      />
                    </div>
                  </div>

                  {/* Applies To */}
                  <div>
                    <label className="block text-xs font-semibold text-slate-700 dark:text-slate-300 mb-1.5">
                      Booking Channels Applicable
                    </label>
                    <div className="grid grid-cols-3 gap-1.5 p-1 bg-slate-100 dark:bg-slate-800 rounded-lg text-xs font-semibold">
                      <button
                        type="button"
                        onClick={() => setChargeAppliesTo('both')}
                        className={`py-1.5 px-3 rounded-md transition-colors text-center ${
                          chargeAppliesTo === 'both'
                            ? 'bg-white dark:bg-slate-700 text-primary shadow-xs'
                            : 'text-slate-600 dark:text-slate-400 hover:text-slate-900'
                        }`}
                      >
                        Both Channels
                      </button>
                      <button
                        type="button"
                        onClick={() => setChargeAppliesTo('online')}
                        className={`py-1.5 px-3 rounded-md transition-colors text-center ${
                          chargeAppliesTo === 'online'
                            ? 'bg-white dark:bg-slate-700 text-primary shadow-xs'
                            : 'text-slate-600 dark:text-slate-400 hover:text-slate-900'
                        }`}
                      >
                        Online (Customer App)
                      </button>
                      <button
                        type="button"
                        onClick={() => setChargeAppliesTo('offline')}
                        className={`py-1.5 px-3 rounded-md transition-colors text-center ${
                          chargeAppliesTo === 'offline'
                            ? 'bg-white dark:bg-slate-700 text-primary shadow-xs'
                            : 'text-slate-600 dark:text-slate-400 hover:text-slate-900'
                        }`}
                      >
                        Offline (Phone / Walk-in)
                      </button>
                    </div>
                  </div>

                  {/* Checkbox: Apply this rule to all my branches */}
                  {allBranches.length > 1 && (
                    <label className="flex items-center gap-2 cursor-pointer text-xs font-medium text-slate-700 dark:text-slate-300 pt-1">
                      <input
                        type="checkbox"
                        checked={applyToAllBranches}
                        onChange={(e) => setApplyToAllBranches(e.target.checked)}
                        className="rounded accent-primary h-4 w-4"
                      />
                      <span>Apply this rule to all my branches</span>
                    </label>
                  )}

                  {/* Live Examples Preview */}
                  <div className="p-3 rounded-xl bg-blue-50/80 dark:bg-blue-950/30 border border-blue-200/60 dark:border-blue-900/40 text-xs text-blue-900 dark:text-blue-300 space-y-1">
                    <span className="font-bold block">💡 Live Example:</span>
                    {chargeType === 'fixed' ? (
                      (() => {
                        const val = parseFloat(chargeValue) || 500;
                        const adjusted = Math.max(0, 1000 - val);
                        return (
                          <p className="text-[11px] leading-relaxed">
                            Work ₹200 -&gt; customer pays ₹{val}. Work ₹1000 -&gt; ₹{val} adjusted, customer pays ₹{adjusted} more.
                          </p>
                        );
                      })()
                    ) : (
                      (() => {
                        const pct = parseFloat(chargeValue) || 10;
                        const minAmt = (2000 * pct) / 100;
                        return (
                          <p className="text-[11px] leading-relaxed">
                            Work ₹2000 -&gt; minimum ₹{minAmt} ({pct}% of booking total).
                          </p>
                        );
                      })()
                    )}
                  </div>
                </div>
              )}

              <div className="flex justify-end pt-2">
                <Button
                  type="submit"
                  disabled={savingCharge}
                  className="bg-primary hover:bg-primary/90 text-white text-xs h-9 px-5 font-semibold flex items-center gap-1.5"
                >
                  {savingCharge ? <Loader2 size={13} className="animate-spin" /> : <Save size={13} />}
                  <span>Save Booking Charge Settings</span>
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>

        {/* ======================================================== */}
        {/* SECTION 2: PUBLIC CONSUMER APP DISCOVERY & LOCATION      */}
        {/* ======================================================== */}
        <Card className="border border-slate-200 dark:border-slate-800 shadow-sm bg-white dark:bg-slate-900">
          <CardHeader className="p-5 pb-3 border-b border-slate-100 dark:border-slate-800">
            <div className="flex items-center gap-2.5">
              <div className="p-2 rounded-xl bg-emerald-50 dark:bg-emerald-950/40 text-emerald-600 dark:text-emerald-400">
                <Globe size={18} />
              </div>
              <div>
                <CardTitle className="text-sm font-bold text-slate-900 dark:text-white">
                  Online booking (Customer app)
                </CardTitle>
                <CardDescription className="text-xs text-slate-500">
                  Allow customers to browse and book your services directly from the Consumer App.
                </CardDescription>
              </div>
            </div>
          </CardHeader>

          <CardContent className="p-5">
            <form onSubmit={handleSaveBranch} className="space-y-4">
              {/* Online Booking Switch */}
              <div className="flex items-center justify-between p-3.5 rounded-xl bg-emerald-50/50 dark:bg-emerald-950/20 border border-emerald-200/60 dark:border-emerald-800/50">
                <div className="space-y-0.5">
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-bold text-slate-800 dark:text-slate-200">
                      Accept bookings from Public App
                    </span>
                    {isOnlineBookingActive ? (
                      <span className="px-2 py-0.5 rounded text-[10px] font-bold bg-emerald-500/20 text-emerald-600 dark:text-emerald-400">
                        ACTIVE
                      </span>
                    ) : (
                      <span className="px-2 py-0.5 rounded text-[10px] font-bold bg-slate-200 dark:bg-slate-800 text-slate-500">
                        OFF
                      </span>
                    )}
                  </div>
                  <p className="text-[11px] text-slate-500 dark:text-slate-400">
                    ON hone par aapki services app mein serviceable pincodes par dikhengi. Pehle catalog mein services + prices set karein.
                  </p>
                </div>
                <Switch
                  checked={isOnlineBookingActive}
                  onCheckedChange={handleToggleOnlineBooking}
                />
              </div>

              {/* Warning when switch is ON but not publicly visible */}
              {isOnlineBookingActive && !isPubliclyVisible && (
                <div className="p-3 rounded-xl bg-amber-50 dark:bg-amber-950/40 border border-amber-200 dark:border-amber-800 text-amber-800 dark:text-amber-300 text-xs flex items-start gap-2">
                  <AlertCircle size={16} className="text-amber-600 shrink-0 mt-0.5" />
                  <span>Not visible in the customer app yet: Super Admin must enable Home Services for your area and approve the branch.</span>
                </div>
              )}

              {/* Branch Tax State & Location fields */}
              <div className="p-4 rounded-xl bg-slate-50 dark:bg-slate-800/40 border border-slate-200 dark:border-slate-700 space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-xs font-bold text-slate-800 dark:text-slate-200 flex items-center gap-1.5">
                    <MapPin size={14} className="text-primary" />
                    <span>Branch Tax State & Coordinates ({currentBranch?.branch_name || 'Active Branch'})</span>
                  </span>
                  <button
                    type="button"
                    onClick={handleDetectLocation}
                    className="text-[11px] text-primary hover:underline flex items-center gap-1 font-semibold"
                  >
                    <Compass size={12} />
                    <span>Detect GPS</span>
                  </button>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-xs">
                  <div>
                    <label className="block text-[11px] font-semibold text-slate-600 dark:text-slate-300 mb-1">
                      State <span className="text-rose-500">* (Mandatory for GST Calculation)</span>
                    </label>
                    <Input
                      type="text"
                      value={branchState}
                      onChange={e => setBranchState(e.target.value)}
                      placeholder="e.g. Karnataka, Maharashtra, Delhi"
                      required
                      className="h-9 text-xs"
                    />
                    <span className="text-[10px] text-slate-400 mt-0.5 block">
                      Must match valid Indian state for CGST/SGST split.
                    </span>
                  </div>

                  <div>
                    <label className="block text-[11px] font-semibold text-slate-600 dark:text-slate-300 mb-1">
                      Pincode <span className="text-rose-500">*</span>
                    </label>
                    <Input
                      type="text"
                      maxLength={6}
                      value={branchPincode}
                      onChange={e => setBranchPincode(e.target.value)}
                      placeholder="e.g. 560001"
                      className="h-9 text-xs font-mono"
                    />
                  </div>

                  <div className="sm:col-span-2">
                    <label className="block text-[11px] font-semibold text-slate-600 dark:text-slate-300 mb-1">
                      Full Address
                    </label>
                    <Input
                      type="text"
                      value={branchAddress}
                      onChange={e => setBranchAddress(e.target.value)}
                      placeholder="Street, Building, Landmark..."
                      className="h-9 text-xs"
                    />
                  </div>

                  <div>
                    <label className="block text-[11px] font-semibold text-slate-600 dark:text-slate-300 mb-1">
                      City
                    </label>
                    <Input
                      type="text"
                      value={branchCity}
                      onChange={e => setBranchCity(e.target.value)}
                      placeholder="City or Town"
                      className="h-9 text-xs"
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <label className="block text-[11px] font-semibold text-slate-600 dark:text-slate-300 mb-1">
                        Latitude
                      </label>
                      <Input
                        type="number"
                        step="0.000001"
                        value={branchLat}
                        onChange={e => setBranchLat(e.target.value)}
                        placeholder="12.9716"
                        className="h-9 text-xs font-mono"
                      />
                    </div>
                    <div>
                      <label className="block text-[11px] font-semibold text-slate-600 dark:text-slate-300 mb-1">
                        Longitude
                      </label>
                      <Input
                        type="number"
                        step="0.000001"
                        value={branchLng}
                        onChange={e => setBranchLng(e.target.value)}
                        placeholder="77.5946"
                        className="h-9 text-xs font-mono"
                      />
                    </div>
                  </div>
                </div>
              </div>

              <div className="flex justify-end pt-1">
                <Button
                  type="submit"
                  disabled={savingBranch}
                  className="bg-primary hover:bg-primary/90 text-white text-xs h-9 px-5 font-semibold flex items-center gap-1.5"
                >
                  {savingBranch ? <Loader2 size={13} className="animate-spin" /> : <Save size={13} />}
                  <span>Save Location & Discovery</span>
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
        </div>
      )}

      {/* ======================================================== */}
      {/* TAB 3: PRINT SETTINGS                                    */}
      {/* ======================================================== */}
      {activeSettingsTab === 'print' && (
        <div className="w-full">
          <ServicesPrintSettingsView />
        </div>
      )}
    </div>
  );
};

export default ServicesAdminSettings;
